import { SystemRole } from "@/data/use-users-data";
import { GroupRole } from "@/lib/consts";

export interface UserRow {
  email: string;
  role: SystemRole;
  groupRole?: GroupRole;
  groupUuids: string[];
  id: string;
}

export interface UserCreationProgress {
  id: string;
  email: string;
  status: "pending" | "creating" | "completed" | "failed";
  error?: string;
}

export interface EmailTag {
  id: string;
  email: string;
  groupUuids: string[];
}

const EMAIL_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)(?![a-z0-9._+-]*-@)[a-z0-9_+-]+(?:\.[a-z0-9_+-]+)*@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const VALID_ROLES: SystemRole[] = ["user", "super", "bot"];
const EMAIL_DELIMITERS = /[,;\s\t\n]+/;

export const parseEmailsFromText = (
  text: string,
  groupUuids: string[] = [],
): EmailTag[] => {
  const emails = text
    .split(EMAIL_DELIMITERS)
    .map((email) => email.trim())
    .filter(Boolean);

  const emailCounts = new Map<string, number>();
  emails.forEach((email) => {
    emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
  });

  return emails
    .map((email) => {
      return {
        id: `email-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        email,
        groupUuids: [...groupUuids],
      };
    })
    .filter(
      (tag, index, arr) =>
        arr.findIndex((t) => t.email === tag.email) === index,
    );
};

export const emailTagsToUsers = (
  emailTags: EmailTag[],
  role: SystemRole = "user",
): UserRow[] => {
  return emailTags.map((tag) => ({
    id: tag.id,
    email: tag.email,
    role,
    groupUuids: tag.groupUuids,
  }));
};

export const validateUsers = (
  users: UserRow[],
  existingEmails: Set<string>,
  options?: { allowExistingEmail?: boolean },
): { validUsers: UserRow[]; validationErrors: Record<string, string[]> } => {
  const validUsers: UserRow[] = [];
  const validationErrors: Record<string, string[]> = {};

  users.forEach((user) => {
    const userErrors = validateUser(user, existingEmails, options);

    if (userErrors.length > 0) {
      validationErrors[user.id] = userErrors;
    } else {
      validUsers.push(user);
    }
  });

  return { validUsers, validationErrors };
};

export const createUsersWithProgress = async (
  users: UserRow[],
  createUserFn: (
    email: string,
    role: SystemRole,
    groupUuids: string[],
    groupRole?: GroupRole,
  ) => Promise<unknown>,
  updateProgress: (id: string, update: Partial<UserCreationProgress>) => void,
): Promise<{
  successful: Array<{ email: string; uuid: string }>;
  failed: Array<{ email: string; error: string }>;
}> => {
  const results = await Promise.allSettled(
    users.map(async (userData) => {
      updateProgress(userData.id, { status: "creating" });

      const user = await createUserFn(
        userData.email,
        userData.role,
        userData.groupUuids,
        userData.groupRole,
      );

      updateProgress(userData.id, { status: "completed" });

      return { userData, user };
    }),
  );

  const successful: Array<{ email: string; uuid: string }> = [];
  const failed: Array<{ email: string; error: string }> = [];

  results.forEach((result, index) => {
    const userData = users[index];

    if (result.status === "fulfilled") {
      successful.push({
        email: (result.value.user as { email: string; uuid: string }).email,
        uuid: (result.value.user as { email: string; uuid: string }).uuid,
      });
    } else {
      const errorMessage =
        result.reason instanceof Error
          ? result.reason.message
          : "Unknown error";
      failed.push({
        email: userData.email,
        error: errorMessage,
      });
      updateProgress(userData.id, {
        status: "failed",
        error: errorMessage,
      });
    }
  });

  return { successful, failed };
};

export const handleGroupSelection = (
  userId: string,
  groupUuid: string,
  selected: boolean,
  users: UserRow[],
  updateUser: (id: string, updates: Partial<UserRow>) => void,
) => {
  const user = users.find((u) => u.id === userId);
  if (!user) return;

  if (selected) {
    updateUser(userId, {
      groupUuids: [...user.groupUuids, groupUuid],
    });
  } else {
    updateUser(userId, {
      groupUuids: user.groupUuids.filter((uuid) => uuid !== groupUuid),
    });
  }
};

export const handleUserCreationResults = (
  successful: Array<{ email: string; uuid: string }>,
  failed: Array<{ email: string; error: string }>,
  totalUsers: number,
  toast: {
    success: (message: string) => void;
    warning: (message: string) => void;
  },
): void => {
  if (failed.length === 0) {
    const message =
      totalUsers === 1
        ? "User created successfully"
        : `${successful.length} users created successfully`;
    toast.success(message);
  } else if (failed.length === 1) {
    toast.warning(
      successful.length > 0
        ? `Added ${successful.length} of ${totalUsers} users. ${failed[0].error}`
        : failed[0].error,
    );
  } else {
    toast.warning(
      `${successful.length} of ${totalUsers} users created. ${failed.length} failed.`,
    );
  }
};

export const messageFromCreateUserError = (error: unknown): string => {
  if (!error || typeof error !== "object") {
    return "User creation failed";
  }

  const body = error as {
    userResponse?: { message?: string };
    response?: { message?: string };
    message?: string;
  };

  return (
    body.userResponse?.message ||
    body.response?.message ||
    body.message ||
    "User creation failed"
  );
};

export const groupRoleOnSubmit = (role: GroupRole | undefined): GroupRole =>
  role ?? "member";

export const initializeUserProgresses = (
  users: UserRow[],
): Map<string, UserCreationProgress> => {
  const progresses = new Map<string, UserCreationProgress>();
  users.forEach((user) => {
    progresses.set(user.id, {
      id: user.id,
      email: user.email,
      status: "pending",
    });
  });
  return progresses;
};

const validateUser = (
  user: UserRow,
  existingEmails: Set<string>,
  options?: { allowExistingEmail?: boolean },
): string[] => {
  const errors: string[] = [];

  if (!user.email) {
    errors.push("Email is required");
  } else if (!EMAIL_REGEXP.test(user.email)) {
    errors.push("Invalid email format");
  } else if (!options?.allowExistingEmail && existingEmails.has(user.email)) {
    errors.push("Email already exists");
  }

  if (!VALID_ROLES.includes(user.role)) {
    errors.push("Invalid or missing role");
  }

  if (user.groupUuids.length === 0) {
    errors.push("Missing groups");
  }

  return errors;
};
